import React, { useState } from "react";
import {
  FiAlertTriangle,
  FiArrowDownRight,
  FiArrowUpRight,
  FiCreditCard,
  FiGlobe,
  FiMoon,
  FiShield,
  FiSun,
  FiTrash2,
  FiXCircle,
} from "react-icons/fi";
import { DASHBOARD_ASSETS } from "../../constants/dashboard.ts";
import { DashboardSidebar, DashboardTopbar } from "./page.tsx";

type DashboardAccountProps = {
  preview?: boolean;
};

const plans = [
  { id: "basic", name: "Básico", price: "$19" },
  { id: "pro", name: "Pro", price: "$29", current: true },
  { id: "elite", name: "Elite", price: "$79" },
];

export default function DashboardAccount({ preview = false }: DashboardAccountProps) {
  const [name, setName] = useState("Trader");
  const [saved, setSaved] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [favorites, setFavorites] = useState(["EURUSD", "GBPUSD", "USDJPY"]);

  const saveProfile = () => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1600);
  };

  const toggleFavorite = (pair: string) => {
    setFavorites((current) =>
      current.includes(pair)
        ? current.filter((item) => item !== pair)
        : [...current, pair]
    );
  };

  return (
    <div className="min-h-screen bg-[#f4f7f1] text-[#0d2a1f]">
      <DashboardSidebar active="Conta" preview={preview} />

      <main className="min-h-screen xl:pl-[270px]">
        <DashboardTopbar />

        <div className="mx-auto max-w-6xl px-5 py-7 xl:px-6">
          <div className="mb-6">
            <h1 className="text-3xl font-black tracking-[-0.04em] text-[#0d2a1f]">
              A minha conta
            </h1>
            <p className="mt-1.5 text-sm font-medium text-[#73827b]">
              Gere o teu perfil, subscrição e preferências.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <AccountCard title="Perfil">
              <div className="space-y-4">
                <Field label="Nome">
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="h-11 w-full rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-sm font-semibold text-[#0d2a1f] outline-none transition focus:border-[#20c77a] focus:ring-4 focus:ring-[#20c77a]/10"
                  />
                </Field>

                <Field
                  label="Email"
                  hint={
                    <span className="rounded-full bg-[#dff6ea] px-3 py-1 text-xs font-black text-[#087a45]">
                      Verificado
                    </span>
                  }
                >
                  <input
                    value="trader@gmail.com"
                    disabled
                    className="h-11 w-full rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-sm font-semibold text-[#0d2a1f] outline-none"
                  />
                </Field>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={saveProfile}
                    className="h-10 rounded-full bg-[#20c77a] px-5 text-sm font-black text-[#03120e] shadow-[0_10px_24px_rgba(32,199,122,0.2)] transition hover:bg-[#18b56d]"
                  >
                    Guardar alterações
                  </button>
                  {saved && (
                    <span className="text-sm font-black text-[#087a45]">
                      Guardado
                    </span>
                  )}
                </div>
              </div>
            </AccountCard>

            <AccountCard title="Segurança">
              <p className="text-sm font-medium text-[#73827b]">
                A password é gerida em segurança pelo backend.
              </p>
              <button
                type="button"
                className="mt-5 inline-flex h-10 items-center gap-2 rounded-full border border-[#dfe8e1] bg-white px-4 text-sm font-black text-[#0d2a1f] transition hover:border-[#bfe8d3] hover:bg-[#f8fbf6]"
              >
                <FiShield className="h-4 w-4" />
                Alterar password
              </button>
              <div className="mt-4 rounded-xl bg-[#f4f7f1] px-4 py-3 text-sm font-semibold text-[#73827b]">
                Sessão iniciada · membro desde 17/05/2026
              </div>
            </AccountCard>

            <AccountCard title="Preferências de trading">
              <Field label="Nível de experiência">
                <select className="h-11 w-full rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-sm font-semibold text-[#0d2a1f] outline-none transition focus:border-[#20c77a] focus:ring-4 focus:ring-[#20c77a]/10">
                  <option>Intermédio</option>
                  <option>Iniciante</option>
                  <option>Avançado</option>
                </select>
              </Field>

              <div className="mt-4">
                <span className="mb-2 block text-sm font-black text-[#0d2a1f]">
                  Ativos favoritos
                </span>
                <div className="flex flex-wrap gap-2">
                  {DASHBOARD_ASSETS.map((pair) => {
                    const selected = favorites.includes(pair);
                    return (
                      <button
                        key={pair}
                        type="button"
                        onClick={() => toggleFavorite(pair)}
                        className={`rounded-full px-3 py-1.5 text-xs font-black transition ${
                          selected
                            ? "bg-[#20c77a] text-[#03120e]"
                            : "bg-[#eef2e9] text-[#73827b] hover:text-[#0d2a1f]"
                        }`}
                      >
                        {pair}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Expiração preferida" className="mt-4">
                <select className="h-11 w-full rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-sm font-semibold text-[#0d2a1f] outline-none transition focus:border-[#20c77a] focus:ring-4 focus:ring-[#20c77a]/10">
                  <option>M5 · 5 min</option>
                </select>
              </Field>
            </AccountCard>

            <AccountCard title="Aparência e idioma">
              <div>
                <span className="mb-2 block text-sm font-black text-[#0d2a1f]">
                  Tema
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <ThemeButton
                    active={theme === "light"}
                    icon={FiSun}
                    label="Claro"
                    onClick={() => setTheme("light")}
                  />
                  <ThemeButton
                    active={theme === "dark"}
                    icon={FiMoon}
                    label="Escuro"
                    onClick={() => setTheme("dark")}
                  />
                </div>
              </div>

              <Field label="Idioma" className="mt-5">
                <select className="h-11 w-full rounded-xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-sm font-semibold text-[#0d2a1f] outline-none transition focus:border-[#20c77a] focus:ring-4 focus:ring-[#20c77a]/10">
                  <option>Português (Moçambique)</option>
                  <option>Português (Portugal)</option>
                  <option>English</option>
                </select>
              </Field>
            </AccountCard>
          </div>

          <AccountCard className="mt-5" title="Subscrição e faturação">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-[#73827b]">
                Plano atual: <strong className="text-[#0d2a1f]">Pro</strong> · mensal · $29/mês
              </p>
              <span className="rounded-full bg-[#dff6ea] px-3 py-1 text-xs font-black text-[#087a45]">
                Renovação automática ativa
              </span>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`rounded-2xl border p-4 ${
                    plan.current
                      ? "border-[#20c77a] ring-2 ring-[#20c77a]/10"
                      : "border-[#dfe8e1]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-base font-black text-[#0d2a1f]">
                      {plan.name}
                    </span>
                    {plan.current && (
                      <span className="rounded-full bg-[#dff6ea] px-3 py-1 text-xs font-black text-[#087a45]">
                        Atual
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-2xl font-black tracking-[-0.04em] text-[#0d2a1f]">
                    {plan.price}
                  </p>
                  {!plan.current && (
                    <button
                      type="button"
                      className={`mt-4 flex h-9 w-full items-center justify-center gap-2 rounded-full text-sm font-black transition ${
                        plan.id === "basic"
                          ? "border border-[#dfe8e1] bg-white text-[#0d2a1f] hover:border-[#bfe8d3]"
                          : "bg-[#20c77a] text-[#03120e] hover:bg-[#18b56d]"
                      }`}
                    >
                      {plan.id === "basic" ? (
                        <>
                          <FiArrowDownRight className="h-4 w-4" />
                          Mudar para Básico
                        </>
                      ) : (
                        <>
                          <FiArrowUpRight className="h-4 w-4" />
                          Upgrade
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-3 border-t border-[#dfe8e1] pt-4">
              <GhostButton icon={FiCreditCard} label="Métodos de pagamento" />
              <GhostButton icon={FiGlobe} label="Faturas e recibos" />
              <GhostButton danger icon={FiXCircle} label="Cancelar subscrição" />
            </div>
            <p className="mt-3 text-xs font-semibold text-[#73827b]">
              Reembolsos conforme a política de subscrição. Ao cancelar, manténs o acesso até ao fim do ciclo já pago.
            </p>
          </AccountCard>

          <section className="mt-5 rounded-[20px] border border-[#ffc9d6] bg-white p-4 shadow-[0_12px_30px_rgba(190,18,60,0.06)]">
            <h2 className="text-base font-black text-[#e11d48]">
              Eliminar conta
            </h2>
            <p className="mt-1 text-sm font-medium text-[#73827b]">
              Esta ação é permanente e remove todos os teus dados.
            </p>
            <button
              type="button"
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-full bg-[#ff2b5f] px-5 text-sm font-black text-white transition hover:bg-[#e11d48]"
            >
              <FiTrash2 className="h-4 w-4" />
              Eliminar a minha conta
            </button>
          </section>
        </div>

        <div className="border-t border-[#dfe8e1] bg-[#f4f7f1]/95 px-5 py-4 xl:px-8">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 text-xs font-semibold leading-relaxed text-[#73827b] md:flex-row md:items-center md:justify-between">
            <p className="flex items-start gap-2">
              <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#f08700]" />
              As opções binárias envolvem risco elevado. Os sinais são informativos e não constituem aconselhamento financeiro.
            </p>
            <p className="font-black text-[#73827b]">
              Restrição 18+. Dados de demonstração fictícios.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function AccountCard({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-[20px] border border-[#dfe8e1] bg-white p-4 shadow-[0_12px_30px_rgba(20,44,33,0.07)] ${className}`}
    >
      <h2 className="mb-4 text-base font-black text-[#0d2a1f]">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
  className = "",
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 flex items-center justify-between gap-3 text-sm font-black text-[#0d2a1f]">
        {label}
        {hint}
      </span>
      {children}
    </label>
  );
}

function ThemeButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-black transition ${
        active
          ? "border-[#20c77a] bg-white text-[#0d2a1f] ring-4 ring-[#20c77a]/10"
          : "border-[#dfe8e1] bg-white text-[#73827b] hover:text-[#0d2a1f]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function GhostButton({
  icon: Icon,
  label,
  danger = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={`inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm font-black transition hover:bg-[#f4f7f1] ${
        danger ? "text-[#ff2b5f]" : "text-[#73827b] hover:text-[#0d2a1f]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
